import React, { useState, useEffect, useCallback, useRef } from "react";
import { Layout, Button, DatePicker, Modal, Tag, Typography, message, Card, Table, Row, Col, Popconfirm } from "antd";
import TopHeader from "../common/TopHeader/TopHeader";
import Sidebar from "../common/Sidebar/Sidebar";
// import FooterDashboard from "../common/FooterDashboard/FooterDashboard";
import moment from "moment";
import { EditOutlined, FileTextOutlined, ClockCircleOutlined, CheckCircleOutlined, CalendarOutlined } from "@ant-design/icons";
import CleaningTraceabilityApi from "../common/Apis/CleaningTraceabilityApi";
import AddNoteModal from "../common/Models/NoteModal/AddNoteModal";

const { Title, Text } = Typography;
const { Content } = Layout;

const CleaningTraceabilityDepartment = () => {
    const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
    const urlParams = new URLSearchParams(window.location.search);
    const departmentId = urlParams.get("id");
    
    // Add a ref to track if initial load has been done
    const initialLoadDone = useRef(false);
    
    // Role checking
    const [roleCode, setRoleCode] = useState("");
    const isSystemUser = roleCode === "SYSTEM_USER";
    const isManager = roleCode === "MANAGER";
    
    // State for tasks
    const [tasks, setTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [departmentName, setDepartmentName] = useState("");
    
    // State for Add Note Modal (for MANAGER)
    const [noteModalVisible, setNoteModalVisible] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    
    // State for View Note Modal (for SYSTEM_USER)
    const [viewNoteModalVisible, setViewNoteModalVisible] = useState(false);
    const [viewingTask, setViewingTask] = useState(null);
    
    // Date filter state - initialize as null (no pre-filled date)
    const [selectedDate, setSelectedDate] = useState(null);
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    
    // Loading state for mark complete button
    const [markCompleteLoading, setMarkCompleteLoading] = useState(false);

    // Get user role
    useEffect(() => {
        const userDetailsString = localStorage.getItem("decodedToken");
        if (userDetailsString) {
            const userDetails = JSON.parse(userDetailsString);
            const firstRoleDTO = userDetails.roles && userDetails.roles[0];
            if (firstRoleDTO) {
                setRoleCode(firstRoleDTO);
                console.log("Current user role:", firstRoleDTO);
            }
        }
    }, []);

    // Function to convert date to UTC timestamp (start of day)
    const getUTCDateTimestamp = useCallback((date) => {
        // Create UTC date at midnight (00:00:00)
        const utcDate = Date.UTC(date.year(), date.month(), date.date());
        return utcDate;
    }, []);

    // Function to get current date in UTC format
    const getCurrentUTCTimestamp = useCallback(() => {
        const now = moment();
        return Date.UTC(now.year(), now.month(), now.date());
    }, []);

    // Fetch department name from API - memoized with useCallback
    const fetchDepartmentName = useCallback(async () => {
        if (!departmentId) return;
        
        try {
            const response = await CleaningTraceabilityApi.ListCleaningTraceabilityCommon('daily');
            if (response?.payload) {
                const department = response.payload.find(
                    item => item.institutionDTO?.id === parseInt(departmentId)
                );
                if (department?.institutionDTO?.institutionName) {
                    setDepartmentName(department.institutionDTO.institutionName);
                } else {
                    setDepartmentName(`Department ${departmentId}`);
                }
            } else {
                setDepartmentName(`Department ${departmentId}`);
            }
        } catch (error) {
            console.error("Error fetching department name:", error);
            setDepartmentName(`Department ${departmentId}`);
        }
    }, [departmentId]);

    // Function to mark task as completed (for SYSTEM_USER and MANAGER)
    const markTaskAsCompleted = async (taskId, frequency) => {
        setMarkCompleteLoading(true);
        try {
            const formData = JSON.stringify({
                id: taskId,
                frequency: frequency,
                tenantDTO: {
                    id: decodedToken?.tenantId,
                    tenantApiKey: decodedToken?.tenantApiKey,
                },
            });
            console.log("Marking task as completed with data:", formData);
            const response = await CleaningTraceabilityApi.TasksMarkComplete(taskId, formData);

            if (response) {
                // Update the task status in the current tasks list
                setTasks(prevTasks => 
                    prevTasks.map(task => 
                        task.id === taskId ? { ...task, status: "completed" } : task
                    )
                );
                message.success("Task marked as completed successfully!");
            }
        } catch (err) {
            console.error("Error marking task as completed:", err);
            message.error(err?.apiError?.message || "Failed to mark task as completed");
        } finally {
            setMarkCompleteLoading(false);
        }
    };

    // Fetch tasks for the department - memoized with useCallback
    const fetchTasksForDepartment = useCallback(async () => {
        if (!departmentId) return;
        
        setIsLoading(true);
        try {
            // Always use current date in UTC format
            const currentUTCTimestamp = getCurrentUTCTimestamp();
            
            const frequencies = ["DAILY", "WEEKLY", "MONTHLY"];
            let allTasks = [];
            
            for (const frequency of frequencies) {
                try {
                    const response = await CleaningTraceabilityApi.ListCleaningTraceabilityForToday(
                        currentUTCTimestamp,
                        frequency
                    );
                    
                    if (response?.payload && response.payload.length > 0) {
                        const departmentTasks = response.payload
                            .filter(task => task.institutionDTO?.id === parseInt(departmentId))
                            .map(task => ({
                                id: task.id,
                                taskName: task.taskMasterDTO?.taskName || "N/A",
                                department: task.institutionDTO?.institutionName,
                                departmentId: task.institutionDTO?.id,
                                area: task.taskMasterDTO?.areaDTO?.areaName || task.taskMasterDTO?.areaDTO?.name || "N/A",
                                frequency: task.taskMasterDTO?.frequency,
                                description: task.taskMasterDTO?.description || "-",
                                status: task.isCompleted ? "completed" : "pending",
                                priority: task.priority || "medium",
                                scheduledDate: task.scheduledDate,
                                managerNote: task.managerNote || "",
                                originalData: task,
                            }));
                        allTasks = [...allTasks, ...departmentTasks];
                        
                        if (departmentTasks.length > 0 && departmentTasks[0].department && departmentName === `Department ${departmentId}`) {
                            setDepartmentName(departmentTasks[0].department);
                        }
                    }
                } catch (error) {
                    console.error(`Error fetching ${frequency} tasks:`, error);
                }
            }
            
            setTasks(allTasks);
            
            if (allTasks.length === 0) {
                message.info(`No tasks found for today`);
            }
        } catch (err) {
            console.error("Error fetching tasks:", err);
            message.error("Failed to fetch tasks");
        } finally {
            setIsLoading(false);
        }
    }, [departmentId, getCurrentUTCTimestamp, departmentName]);

    // Fetch historical tasks for selected date - memoized with useCallback
    const fetchHistoricalTasks = useCallback(async (date) => {
        if (!departmentId || !date) return;
        
        setIsLoading(true);
        try {
            // Convert selected date to UTC timestamp
            const utcTimestamp = getUTCDateTimestamp(date);
            
            const frequencies = ["DAILY", "WEEKLY", "MONTHLY"];
            let allTasks = [];
            
            for (const frequency of frequencies) {
                try {
                    const response = await CleaningTraceabilityApi.ListCleaningTraceabilityForToday(
                        utcTimestamp,
                        frequency
                    );
                    
                    if (response?.payload && response.payload.length > 0) {
                        const departmentTasks = response.payload
                            .filter(task => task.institutionDTO?.id === parseInt(departmentId))
                            .map(task => ({
                                id: task.id,
                                taskName: task.taskMasterDTO?.taskName || "N/A",
                                department: task.institutionDTO?.institutionName,
                                departmentId: task.institutionDTO?.id,
                                area: task.taskMasterDTO?.areaDTO?.areaName || task.taskMasterDTO?.areaDTO?.name || "N/A",
                                frequency: task.taskMasterDTO?.frequency,
                                description: task.taskMasterDTO?.description || "-",
                                status: task.isCompleted ? "completed" : "pending",
                                priority: task.priority || "medium",
                                scheduledDate: task.scheduledDate,
                                managerNote: task.managerNote || "",
                                originalData: task,
                            }));
                        allTasks = [...allTasks, ...departmentTasks];
                    }
                } catch (error) {
                    console.error(`Error fetching ${frequency} tasks:`, error);
                }
            }
            
            setTasks(allTasks);
            
            if (allTasks.length === 0) {
                message.info(`No tasks found for ${date.format("DD/MM/YYYY")}`);
            }
        } catch (err) {
            console.error("Error fetching historical tasks:", err);
            message.error("Failed to fetch tasks for selected date");
        } finally {
            setIsLoading(false);
        }
    }, [departmentId, getUTCDateTimestamp]);

    // Handle date change - only fetch when user selects a date
    const handleDateChange = useCallback((date) => {
        if (date) {
            setSelectedDate(date);
            fetchHistoricalTasks(date);
            setCurrentPage(1);
        } else {
            // If date is cleared, reset to current date tasks
            setSelectedDate(null);
            fetchTasksForDepartment();
            setCurrentPage(1);
        }
    }, [fetchHistoricalTasks, fetchTasksForDepartment]);

    // Disable future dates
    const disabledDate = (current) => {
        if (!current) return false;
        // Disable any date after today
        const today = moment().startOf('day');
        return current.isAfter(today);
    };

    // Reset to current date
    const handleResetToToday = useCallback(() => {
        setSelectedDate(null);
        fetchTasksForDepartment();
        setCurrentPage(1);
        message.success("Showing today's tasks");
    }, [fetchTasksForDepartment]);

    // Function to open note modal (for MANAGER)
    const openNoteModal = (task) => {
        setSelectedTask(task);
        setNoteModalVisible(true);
    };

    // Function to view note (for SYSTEM_USER)
    const viewNote = (task) => {
        setViewingTask(task);
        setViewNoteModalVisible(true);
    };

    // Handle note added callback
    const handleNoteAdded = useCallback((taskId, newNote) => {
        console.log(`Note added/updated for task ${taskId}:`, newNote);
        
        setTasks(prevTasks => 
            prevTasks.map(task => 
                task.id === taskId 
                    ? { ...task, managerNote: newNote.managerNote }
                    : task
            )
        );
        
        if (selectedTask && selectedTask.id === taskId) {
            setSelectedTask(prev => ({ ...prev, managerNote: newNote.managerNote }));
        }
        
        message.success("Note updated successfully");
    }, [selectedTask]);

    // Initial data fetch - runs only when departmentId changes
    useEffect(() => {
        if (departmentId && !initialLoadDone.current) {
            initialLoadDone.current = true;
            fetchDepartmentName();
            fetchTasksForDepartment();
        }
    }, [departmentId, fetchDepartmentName, fetchTasksForDepartment]);

    // Table columns
    const getTaskColumns = useCallback(() => {
        const columns = [
            {
                title: "Task Name",
                dataIndex: "taskName",
                key: "taskName",
                render: (text, record) => (
                    <div>
                        <Text strong style={{ textDecoration: record.status === "completed" ? "line-through" : "none" }}>
                            {text}
                        </Text>
                        {record.priority === "high" && record.status !== "completed" && (
                            <Tag color="red" size="small" style={{ marginLeft: 8 }}>High Priority</Tag>
                        )}
                    </div>
                ),
            },
            {
                title: "Area",
                dataIndex: "area",
                key: "area",
                render: (text) => text || "-",
            },
            {
                title: "Frequency",
                dataIndex: "frequency",
                key: "frequency",
                render: (text) => (
                    <Tag color={text === "DAILY" ? "blue" : text === "WEEKLY" ? "green" : "orange"}>
                        {text}
                    </Tag>
                ),
            },
            {
                title: "Status",
                dataIndex: "status",
                key: "status",
                render: (text) => {
                    if (text === "pending") {
                        return (
                            <Tag color="orange" icon={<ClockCircleOutlined />}>
                                PENDING
                            </Tag>
                        );
                    }
                    if (text === "completed") {
                        return (
                            <Tag color="green" icon={<CheckCircleOutlined />}>
                                COMPLETED
                            </Tag>
                        );
                    }
                    return <Tag>{text}</Tag>;
                },
            },
        ];

        // Add Manager Note column for SYSTEM_USER
        if (isSystemUser) {
            columns.push({
                title: "Manager Note",
                key: "managerNote",
                render: (_, record) => (
                    <div>
                        {record.managerNote ? (
                            <Button
                                type="link"
                                size="small"
                                icon={<FileTextOutlined />}
                                onClick={() => viewNote(record)}
                                style={{ padding: 0 }}
                            >
                                View Note
                            </Button>
                        ) : (
                            <Tag color="default">No Note</Tag>
                        )}
                    </div>
                ),
            });
        }

        // Add Action column
        columns.push({
            title: "Action",
            key: "action",
            render: (_, record) => (
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {/* Mark Complete button for SYSTEM_USER and MANAGER - only for pending tasks */}
                    {(isSystemUser || isManager) && record.status === "pending" && (
                        <Popconfirm
                            title="Mark as Completed"
                            description="Are you sure you want to mark this task as completed?"
                            onConfirm={() => markTaskAsCompleted(record.id, record.frequency)}
                            okText="Yes"
                            cancelText="No"
                            placement="topRight"
                        >
                            <Button
                                type="primary"
                                size="small"
                                icon={<CheckCircleOutlined />}
                                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                                loading={markCompleteLoading}
                            >
                                Mark Complete
                            </Button>
                        </Popconfirm>
                    )}
                    
                    {/* Add/Update Note button for MANAGER */}
                    {isManager && (
                        <Button
                            type="default"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => openNoteModal(record)}
                            style={{ color: '#1890ff', borderColor: '#1890ff' }}
                        >
                            {record.managerNote ? "Update Note" : "Add Note"}
                        </Button>
                    )}
                    
                    {/* If task is completed, show a badge for SYSTEM_USER */}
                    {record.status === "completed" && !isManager && !isSystemUser && (
                        <Tag color="green" icon={<CheckCircleOutlined />}>
                            Completed
                        </Tag>
                    )}
                </div>
            ),
        });

        return columns;
    }, [isSystemUser, isManager, markCompleteLoading, markTaskAsCompleted]);

    const paginatedData = tasks.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    return (
        <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
            <Sidebar />
            <Layout>
                <TopHeader />
                
                {isLoading && (
                    <div className="spinner-overlay" id="spinnerOverlay">
                        <div className="spinner"></div>
                    </div>
                )}
                
                <Content style={{ margin: "24px 16px 0", padding: "24px", background: "white", borderRadius: 12 }}>
                    <Card
                        title="Filters"
                        style={{ marginBottom: 24, borderRadius: 12 }}
                        extra={
                            <Button onClick={handleResetToToday}>
                                Show Today's Tasks
                            </Button>
                        }
                    >
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={8}>
                                <div>
                                    <Text strong>Select Date (Optional)</Text>
                                    <DatePicker
                                        format="DD/MM/YYYY"
                                        style={{ width: '100%', marginTop: 8 }}
                                        onChange={handleDateChange}
                                        value={selectedDate}
                                        placeholder="Pick a date to view history"
                                        disabledDate={disabledDate}
                                        allowClear={true}
                                    />
                                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                                        Leave empty to view today's tasks
                                    </Text>
                                </div>
                            </Col>
                            <Col xs={24} sm={12} md={8}>
                                <div>
                                    <Text strong>Showing Tasks For</Text>
                                    <div style={{ marginTop: 8 }}>
                                        <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px' }}>
                                            {selectedDate ? selectedDate.format("DD/MM/YYYY") : "Today's Date"}
                                        </Tag>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </Card>

                    <Card
                        title={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <CalendarOutlined style={{ color: '#1890ff' }} />
                                <span>Department Tasks - {selectedDate ? selectedDate.format("DD/MM/YYYY") : moment().format("DD/MM/YYYY")}</span>
                            </div>
                        }
                        style={{ borderRadius: 12 }}
                    >
                        <Table
                            columns={getTaskColumns()}
                            dataSource={paginatedData}
                            loading={isLoading}
                            rowKey="id"
                            pagination={{
                                current: currentPage,
                                pageSize: pageSize,
                                total: tasks.length,
                                onChange: (page, size) => {
                                    setCurrentPage(page);
                                    if (size !== pageSize) setPageSize(size);
                                },
                                showSizeChanger: true,
                                showTotal: (total) => `Total ${total} tasks`,
                            }}
                            locale={{
                                emptyText: 'No tasks found for this department on the selected date',
                            }}
                        />
                    </Card>
                </Content>
                
                {/* <FooterDashboard /> */}
            </Layout>

            <AddNoteModal
                visible={noteModalVisible}
                task={selectedTask}
                onClose={() => {
                    setNoteModalVisible(false);
                    setSelectedTask(null);
                }}
                onNoteAdded={handleNoteAdded}
                currentUser={decodedToken}
                apiService={CleaningTraceabilityApi}
            />

            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FileTextOutlined style={{ color: '#1890ff' }} />
                        <span>Manager's Note</span>
                    </div>
                }
                open={viewNoteModalVisible}
                onCancel={() => {
                    setViewNoteModalVisible(false);
                    setViewingTask(null);
                }}
                footer={[
                    <Button key="close" onClick={() => {
                        setViewNoteModalVisible(false);
                        setViewingTask(null);
                    }}>
                        Close
                    </Button>,
                ]}
                width={600}
            >
                {viewingTask && (
                    <div>
                        <div style={{ marginBottom: 20, padding: '16px', background: '#f5f5f5', borderRadius: 8 }}>
                            <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 12 }}>
                                Task Details:
                            </Text>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                <Tag color="blue" style={{ fontSize: '13px', padding: '4px 12px' }}>
                                    {viewingTask.taskName}
                                </Tag>
                                <Tag color="geekblue" style={{ fontSize: '13px', padding: '4px 12px' }}>
                                    {viewingTask.department || "N/A"}
                                </Tag>
                                <Tag color="cyan" style={{ fontSize: '13px', padding: '4px 12px' }}>
                                    {viewingTask.area || "N/A"}
                                </Tag>
                                <Tag 
                                    color={viewingTask.status === "completed" ? "green" : "orange"} 
                                    style={{ fontSize: '13px', padding: '4px 12px' }}
                                >
                                    {viewingTask.status?.toUpperCase() || "PENDING"}
                                </Tag>
                                {viewingTask.frequency && (
                                    <Tag 
                                        color={viewingTask.frequency === "DAILY" ? "blue" : viewingTask.frequency === "WEEKLY" ? "green" : "orange"}
                                        style={{ fontSize: '13px', padding: '4px 12px' }}
                                    >
                                        {viewingTask.frequency}
                                    </Tag>
                                )}
                            </div>
                        </div>

                        <div style={{ marginBottom: 20 }}>
                            <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 12 }}>
                                Note Content:
                            </Text>
                            <div style={{ 
                                padding: '16px', 
                                background: '#fff7e6', 
                                borderRadius: 8,
                                border: '1px solid #ffd591'
                            }}>
                                <Text style={{ fontSize: 14, lineHeight: 1.6 }}>
                                    {viewingTask.managerNote || "No note has been added for this task yet."}
                                </Text>
                            </div>
                        </div>

                        <div style={{ 
                            marginTop: 16, 
                            padding: '8px 12px', 
                            background: '#e6f7ff', 
                            borderRadius: 6,
                            border: '1px solid #91d5ff'
                        }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                💡 This note was added by the manager for reference and quality tracking purposes.
                            </Text>
                        </div>
                    </div>
                )}
            </Modal>
        </Layout>
    );
};

export default CleaningTraceabilityDepartment;